/**
 * The recipient-facing "what the budget covers" box (§A6f) — the EXACT content a
 * bid approver sees. Shared by the public approval page and the supervisor's
 * in-app "preview what the recipient sees" so the two can never drift. Shows the
 * crew (size + role mix) and man-hours for labor, the non-labor components, and
 * the grand total — never per-line dollars or rates.
 */
import type { CrewRoleLines } from '@vari-crew/schemas';
import { formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';

const ROLE_LABELS: Record<keyof CrewRoleLines, [string, string]> = {
  lead: ['lead', 'leads'],
  installer: ['installer', 'installers'],
  driver: ['driver', 'drivers'],
  trainee: ['trainee', 'trainees'],
};

/** "1 lead · 2 installers" — omits zero roles; null when no mix given. */
export function roleSummary(roles: CrewRoleLines | null | undefined): string | null {
  if (!roles) return null;
  const parts = (Object.keys(ROLE_LABELS) as Array<keyof CrewRoleLines>)
    .filter((k) => (roles[k]?.count ?? 0) > 0)
    .map((k) => `${roles[k].count} ${ROLE_LABELS[k][roles[k].count === 1 ? 0 : 1]}`);
  return parts.length ? parts.join(' · ') : null;
}

export interface BudgetCoverageProps {
  showCost: boolean;
  crew?: number | null;
  manHours?: number | null;
  roles?: CrewRoleLines | null;
  includesMileage: boolean;
  includesDebris: boolean;
  totalCents: number;
  className?: string;
}

export function BudgetCoverage({
  showCost,
  crew,
  manHours,
  roles,
  includesMileage,
  includesDebris,
  totalCents,
  className,
}: BudgetCoverageProps) {
  if (!showCost) {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-fill-soft p-3 text-sm text-ink-dim',
          className,
        )}
      >
        No cost to approve — this is a date sign-off.
      </div>
    );
  }
  const roleStr = roleSummary(roles);
  const laborDetail = [
    crew != null ? `crew of ${crew}` : null,
    manHours != null ? `${manHours} man-hours` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={cn('rounded-lg border border-line bg-fill-soft p-3 space-y-2', className)}>
      <div className="text-xs font-medium uppercase tracking-wide text-ink-faint">
        What the budget covers
      </div>
      <ul className="text-sm space-y-1 list-disc pl-5 marker:text-ink-faint">
        <li>
          Labor
          {laborDetail && <span className="text-ink-faint"> · {laborDetail}</span>}
          {roleStr && <div className="text-[11px] text-ink-faint">{roleStr}</div>}
        </li>
        {includesMileage && <li>Mileage &amp; travel</li>}
        {includesDebris && <li>Debris removal</li>}
      </ul>
      <div className="flex items-center justify-between border-t border-line pt-2">
        <span className="text-sm font-medium">Total cost</span>
        <span className="text-2xl font-semibold font-mono">{formatMoneyCents(totalCents)}</span>
      </div>
    </div>
  );
}
